import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AccountTypeValues, type AccountType as AccountTypeValue } from '@paladin-monorepo/common';

import API from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  exhibitionTypeDialogDefault,
  isExhibitionTypeConfirmed,
  markExhibitionTypeConfirmed,
  needsExhibitionTypeDialog,
} from '@/lib/exhibition';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * Shown on /exhibition/start after instant signup (?exhibition=1) or when the
 * account type is not inquirer yet. Confirms inquirer by default; visitor can change it.
 */
export function ExhibitionAccountTypeDialog({
  onConfirmed,
}: {
  onConfirmed?: () => void;
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth({ tmp: 'ExhibitionAccountTypeDialog' });
  const [selected, setSelected] = useState<AccountTypeValue | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isExhibitionArrival = searchParams.get('exhibition') === '1';
  const account = user?.account;
  const alreadyConfirmed = account ? isExhibitionTypeConfirmed(account.id) : false;
  const open =
    !dismissed &&
    !alreadyConfirmed &&
    needsExhibitionTypeDialog(user, isExhibitionArrival);

  if (!open || !account) {
    return null;
  }

  const current = selected ?? exhibitionTypeDialogDefault();

  const dismiss = () => {
    markExhibitionTypeConfirmed(account.id);
    setDismissed(true);
    onConfirmed?.();
    const next = new URLSearchParams(searchParams);
    next.delete('exhibition');
    setSearchParams(next, { replace: true });
  };

  const confirm = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (current !== account.accountType) {
        await API.patch(`/account/${account.id}`, { accountType: current });
        await queryClient.refetchQueries({ queryKey: ['/me'] });
        queryClient.invalidateQueries({ queryKey: ['agent-configs'] });
      }
      dismiss();
    } catch {
      toast.error(t('exhibition.typeDialog.error'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(dialogOpen) => {
        if (!dialogOpen && !isSaving) dismiss();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('exhibition.typeDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('exhibition.typeDialog.description', {
              type: t(`exhibition.typeDialog.types.${account.accountType}.label`),
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {AccountTypeValues.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setSelected(type as AccountTypeValue)}
              className={cn(
                'rounded-md border p-3 text-left transition-colors',
                current === type
                  ? 'border-primary bg-primary/5'
                  : 'hover:bg-muted',
              )}
            >
              <span className="font-medium">
                {t(`exhibition.typeDialog.types.${type}.label`)}
              </span>
              <p className="text-sm text-muted-foreground">
                {t(`exhibition.typeDialog.types.${type}.description`)}
              </p>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={confirm} disabled={isSaving}>
            {isSaving
              ? t('exhibition.typeDialog.saving')
              : t('exhibition.typeDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
